import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const links = [
  { to: '/oil-gas', title: 'Oil & Gas', detail: 'Perforating and completion components' },
  { to: '/data-centers', title: 'Data Centers', detail: 'Components, conduit and duct bank systems' },
  { to: '/industrial', title: 'Industrial & Process Equipment', detail: 'Parts for OEMs and machinery makers' },
];

const normalizedPath = (pathname) => {
  const path = pathname.replace(/\.html$/, '').replace(/\/$/, '') || '/';
  return path === '/index' ? '/' : path;
};

export default function Navigation() {
  const location = useLocation();
  const headerRef = useRef(null);
  const toggleRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const path = normalizedPath(location.pathname);
  const industryActive = ['/industries', '/oil-gas', '/data-centers', '/data-center-conduit', '/industrial'].includes(path);

  useEffect(() => {
    setMenuOpen(false);
    setIndustriesOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setIndustriesOpen(false);
        if (menuOpen) toggleRef.current?.focus();
      }
    };
    const onPointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMenuOpen(false);
        setIndustriesOpen(false);
      }
    };
    const desktopBreakpoint = window.matchMedia('(min-width: 1101px)');
    const onBreakpointChange = (event) => {
      if (event.matches) {
        setMenuOpen(false);
        setIndustriesOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    desktopBreakpoint.addEventListener('change', onBreakpointChange);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      desktopBreakpoint.removeEventListener('change', onBreakpointChange);
    };
  }, [menuOpen, industriesOpen]);

  const closeMenus = () => {
    setMenuOpen(false);
    setIndustriesOpen(false);
  };
  const pageLinkClass = (to) => `mx-nav-link${path === to ? ' is-active' : ''}`;

  return (
    <header ref={headerRef} className={`mx-header${menuOpen ? ' nav-open' : ''}`}>
      <Link to="/" className="mx-brand" onClick={closeMenus} aria-label="MachineX America home">
        <img className="mx-brand-logo" src="/media/images/logo-flame-mark.png" alt="MachineX America flame logo" />
        <span className="mx-brand-lockup">
          <span className="mx-brand-name">MACHINEX <span className="mx-brand-country">AMERICA</span><span className="mx-brand-suffix">LLC</span></span>
          <span className="mx-brand-tag">PRECISION MANUFACTURING</span>
        </span>
      </Link>

      <nav className="mx-nav" id="site-nav" aria-label="Main">
        <NavLink to="/" end className={pageLinkClass('/')} onClick={closeMenus}>Home</NavLink>
        <div
          className={`mx-dropdown${industriesOpen ? ' is-open' : ''}`}
          onMouseEnter={() => setIndustriesOpen(true)}
          onMouseLeave={(event) => {
            if (!event.currentTarget.contains(document.activeElement)) setIndustriesOpen(false);
          }}
          onFocus={() => setIndustriesOpen(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget) && !event.currentTarget.matches(':hover')) {
              setIndustriesOpen(false);
            }
          }}
        >
          <button
            type="button"
            className={`mx-dropdown-trigger${industryActive ? ' is-active' : ''}`}
            aria-haspopup="true"
            aria-expanded={menuOpen || industriesOpen}
            onClick={() => setIndustriesOpen(true)}
          >
            Industries
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <div className="mx-industry-menu">
            <div className="mx-menu-inner">
              {links.map(({ to, title, detail }) => (
                <NavLink key={to} to={to} className="mx-menu-link" onClick={closeMenus}>
                  {title}<small>{detail}</small>
                </NavLink>
              ))}
              <NavLink to="/industries" className="mx-menu-link mx-menu-all" onClick={closeMenus}>All industries</NavLink>
            </div>
          </div>
        </div>
        <NavLink to="/about" className={pageLinkClass('/about')} onClick={closeMenus}>About</NavLink>
        <NavLink to="/contact" className={pageLinkClass('/contact')} onClick={closeMenus}>Contact</NavLink>
        <Link to="/contact" className="mx-button mx-nav-cta" onClick={closeMenus}>Get a quote</Link>
      </nav>

      <Link to="/contact" className="mx-button mx-header-quote" onClick={closeMenus}>Get a quote</Link>
      <button
        ref={toggleRef}
        type="button"
        className="mx-menu-toggle"
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        aria-controls="site-nav"
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span /><span /><span />
      </button>
    </header>
  );
}
